import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { subscribe, dismissUpload } from '../lib/uploadQueue';

const statusIcon = (status) => ({
  uploading: FiIcons.FiUploadCloud,
  success:   FiIcons.FiCheckCircle,
  error:     FiIcons.FiAlertCircle,
  offline:   FiIcons.FiWifiOff,
}[status] || FiIcons.FiUploadCloud);

const statusColor = (status) => ({
  uploading: 'text-blue-500',
  success:   'text-green-500',
  error:     'text-red-500',
  offline:   'text-amber-500',
}[status] || 'text-blue-500');

const statusBg = (status) => ({
  uploading: 'bg-blue-50 border-blue-200',
  success:   'bg-green-50 border-green-200',
  error:     'bg-red-50 border-red-200',
  offline:   'bg-amber-50 border-amber-200',
}[status] || 'bg-blue-50 border-blue-200');

const UploadsTray = () => {
  const [uploads, setUploads] = useState([]);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => subscribe(setUploads), []);

  const active    = uploads.filter(u => u.status === 'uploading').length;
  const hasItems  = uploads.length > 0;

  if (!hasItems) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 40 }}
      className="fixed bottom-4 right-4 z-50 w-80 shadow-2xl rounded-2xl overflow-hidden border border-gray-200 bg-white"
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 bg-gray-800 text-white cursor-pointer select-none"
        onClick={() => setCollapsed(c => !c)}
      >
        <div className="flex items-center gap-2">
          {active > 0
            ? <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            : <SafeIcon icon={FiIcons.FiUploadCloud} className="text-sm" />
          }
          <span className="text-sm font-bold">
            {active > 0 ? `Uploading ${active} video${active > 1 ? 's' : ''}…` : `${uploads.length} upload${uploads.length > 1 ? 's' : ''}`}
          </span>
        </div>
        <SafeIcon
          icon={collapsed ? FiIcons.FiChevronUp : FiIcons.FiChevronDown}
          className="text-sm opacity-70"
        />
      </div>

      {/* Items */}
      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }}
            className="overflow-hidden"
          >
            <div className="max-h-72 overflow-y-auto divide-y divide-gray-100">
              {uploads.map(u => (
                <motion.div
                  key={u.id}
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className={`px-4 py-3 flex items-start gap-3 ${statusBg(u.status)} border-b last:border-b-0`}
                >
                  <SafeIcon icon={statusIcon(u.status)} className={`flex-shrink-0 mt-0.5 ${statusColor(u.status)}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-gray-800 truncate">{u.filename}</p>
                    {u.status === 'uploading' ? (
                      <div className="mt-1.5 w-full bg-blue-200 rounded-full h-1">
                        <motion.div
                          className="bg-blue-500 h-1 rounded-full"
                          animate={{ width: `${u.progress || 0}%` }}
                          transition={{ duration: 0.3 }}
                        />
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{u.message}</p>
                    )}
                  </div>
                  {u.status !== 'uploading' && (
                    <button
                      onClick={() => dismissUpload(u.id)}
                      className="flex-shrink-0 opacity-40 hover:opacity-80 transition-opacity"
                    >
                      <SafeIcon icon={FiIcons.FiX} className="text-xs" />
                    </button>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default UploadsTray;